import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';

import { fetcher } from '../utils/fetcher';
import { setTalentToken } from '../utils/talent-token';

const ENDPOINT = process.env.TALENT_API_INTEGRATION;

export const useTalentToken = () => {
  const searchParams = useSearchParams();

  const roleRef = useRef(searchParams.get('role'));
  // flex loads 0 as employeeId when the user don't have an employee
  const employeeIdRef = useRef(searchParams.get('employeeId'));
  const companyIdRef = useRef(searchParams.get('companyId'));

  // If employeeId is not present, get userId and companyId
  const userIdRef = useRef<string | null>(
    !searchParams.get('employeeId') || employeeIdRef.current === '0'
      ? searchParams.get('userId')
      : null,
  );

  const body = {
    employeeId: employeeIdRef.current === '0' ? null : employeeIdRef.current,
    role: roleRef.current,
    companyId: companyIdRef.current,
    userId: userIdRef.current,
  };

  // Enable query only if we have at least employeeId or (userId and companyId)
  const isEnabled =
    Boolean(employeeIdRef.current) ||
    (Boolean(userIdRef.current) && Boolean(companyIdRef.current));

  const { data, isError, error, isPending } = useQuery({
    queryKey: ['talent-token', JSON.stringify(body)],
    queryFn: () =>
      fetcher<{ token: string }>(
        `${ENDPOINT}/learningintegration/security/generate-token-talent`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          skipAuth: true,
        },
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });

  if (isError) {
    console.error(error);
  }

  const token = typeof data === 'object' ? data.token : undefined;

  useEffect(() => {
    setTalentToken(token);
  }, [token]);

  // Store token as Authorization Bearer in window.crehApolloHeaders
  // Merge with existing headers to preserve x-talent-authorization from learning token
  if (token && typeof window !== 'undefined') {
    window.crehApolloHeaders = {
      ...window.crehApolloHeaders,
      'talent-token': token,
    };
  }

  return {
    token,
    isPending,
    isError,
    error,
  };
};
